import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faUserPlus,
  faCheck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { toast, swalConfirm } from '@/lib/swal';
import { inspectionService, type Inspection } from '@/services/inspection.service';

interface PendingOwnershipRequestsProps {
  inspection: Inspection;
  currentUserId: string;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function PendingOwnershipRequests({
  inspection,
  currentUserId,
}: PendingOwnershipRequestsProps) {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['inspection', inspection.id] });

  const respondTransferMutation = useMutation({
    mutationFn: (payload: { accept: boolean; comment?: string }) =>
      inspectionService.respondToTransfer(inspection.id, payload),
    onSuccess: (res) => {
      toast.success(res.message);
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to respond to transfer')),
  });

  const respondTakeoverMutation = useMutation({
    mutationFn: (payload: { accept: boolean; comment?: string }) =>
      inspectionService.respondToTakeover(inspection.id, payload),
    onSuccess: (res) => {
      toast.success(res.message);
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to respond to takeover')),
  });

  const isIncomingTransfer =
    inspection.status === 'TRANSFER_REQUESTED' && inspection.pendingTransferToId === currentUserId;

  const hasPendingTakeover =
    inspection.assignedEngineerId === currentUserId && !!inspection.pendingTakeoverFromId;

  if (!isIncomingTransfer && !hasPendingTakeover) return null;

  const handleTransferAccept = async () => {
    const ok = await swalConfirm({
      title: 'Accept Transfer?',
      text: 'You will become the owner of this inspection.',
      confirmText: 'Accept',
    });
    if (ok) respondTransferMutation.mutate({ accept: true });
  };

  const handleTransferReject = async () => {
    const ok = await swalConfirm({
      title: 'Reject Transfer?',
      text: 'The inspection will remain with the current owner.',
      confirmText: 'Reject',
    });
    if (ok) respondTransferMutation.mutate({ accept: false });
  };

  const handleTakeoverAccept = async () => {
    const ok = await swalConfirm({
      title: 'Accept Takeover Request?',
      text: 'Ownership will transfer to the requesting engineer.',
      confirmText: 'Accept',
    });
    if (ok) respondTakeoverMutation.mutate({ accept: true });
  };

  const handleTakeoverReject = async () => {
    const ok = await swalConfirm({
      title: 'Reject Takeover Request?',
      text: 'The requester will be notified that the takeover was rejected.',
      confirmText: 'Reject',
    });
    if (ok) respondTakeoverMutation.mutate({ accept: false });
  };

  return (
    <div className="space-y-3">
      {isIncomingTransfer && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700 px-5 py-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={faArrowRightArrowLeft}
              className="mt-0.5 h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Incoming Transfer Request
              </p>
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                <span className="font-medium">{inspection.assignedEngineerName}</span> is requesting
                to transfer this inspection to you.
              </p>
              {inspection.pendingTransferReason && (
                <p className="mt-1 text-xs text-muted-foreground italic">
                  Reason: {inspection.pendingTransferReason}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => void handleTransferAccept()}
                  disabled={respondTransferMutation.isPending}
                >
                  <FontAwesomeIcon icon={faCheck} className="mr-1.5 h-3 w-3" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="border-destructive text-destructive hover:bg-destructive/5"
                  onClick={() => void handleTransferReject()}
                  disabled={respondTransferMutation.isPending}
                >
                  <FontAwesomeIcon icon={faXmark} className="mr-1.5 h-3 w-3" />
                  Reject
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPendingTakeover && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/10 dark:border-blue-700 px-5 py-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={faUserPlus}
              className="mt-0.5 h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                Takeover Request Pending
              </p>
              <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">
                An engineer is requesting to take over this inspection from you.
              </p>
              {inspection.pendingTakeoverReason && (
                <p className="mt-1 text-xs text-muted-foreground italic">
                  Their reason: {inspection.pendingTakeoverReason}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => void handleTakeoverAccept()}
                  disabled={respondTakeoverMutation.isPending}
                >
                  <FontAwesomeIcon icon={faCheck} className="mr-1.5 h-3 w-3" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="border-destructive text-destructive hover:bg-destructive/5"
                  onClick={() => void handleTakeoverReject()}
                  disabled={respondTakeoverMutation.isPending}
                >
                  <FontAwesomeIcon icon={faXmark} className="mr-1.5 h-3 w-3" />
                  Reject
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
